import {
    chain, find, get, isEmpty, last,
} from 'lodash';

import React, {
    useCallback, useMemo, memo, useContext,
} from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import MenuItem from '@material-ui/core/MenuItem';
import DeleteIcon from '../../../assets/image/commonIcons/delete-outline.svg';
import CustomSelect from '../../material-ui-kit/CustomSelect/Select';
import useStyle from './EmailSelectComponentStyle';
import UtilsService from '../../../services/UtilsService';
import {
    HOTJAR_TAGS,
    TRACKING_EVENTS,
} from '../../../constants/TrackingConstants';
import TrackingContext from '../../../contexts/TrackingContext';
import PositionContext from '../../../contexts/PositionContext';
import AuthorizationContext, { AuthEntityTypes } from '../../../contexts/AuthorizationContext';

// Modified by AI on 05/26/2026. Edit #1.
const EmailSelectComponent = ({
    className = '',
    emailTemplates = [],
    selectedTemplate = null,
    onSelectedEmailTemplateChange = () => {},
    onDeleteEmailTemplate = () => {},
}) => {
    const { t } = useTranslation();
    const { trackEvent, trackTag } = useContext(TrackingContext);
    const { position } = useContext(PositionContext);
    const { getAuthEntityType } = useContext(AuthorizationContext);
    const classes = useStyle();
    const rootClasses = UtilsService.getClassName([
        className,
        classes.root,
    ]);

    const selectedValue = useMemo(() => get(selectedTemplate, 'uuid', null), [selectedTemplate]);
    const selectOptions = useMemo(() => {
        const entityType = getAuthEntityType();
        const isUserRecruiter = entityType === AuthEntityTypes.RECRUITER;

        const currentJobCampaignOptions = chain(emailTemplates)
            .filter({ isPositionCampaignTemplate: true })
            .map(({ uuid, name, isPositionCampaignTemplate }) => ({
                value: uuid,
                label: name,
                isPositionCampaignTemplate,
            }))
            .value();

        const lastTemplateUsedOptions = (emailTemplates || [])
            .filter((emailTemplate) => emailTemplate.isLastUsedTemplate)
            .map((emailTemplate) => ({
                value: emailTemplate.uuid,
                label: emailTemplate.name,
                isLastUsedTemplate: true,
            }));

        const userEmailOptions = (emailTemplates || [])
            .filter((emailTemplate) => emailTemplate.isUserTemplate && !emailTemplate.isLastUsedTemplate)
            .map((emailTemplate) => ({
                value: emailTemplate.uuid,
                label: emailTemplate.name,
                canDelete: true,
            }));

        const systemEmailOptions = (emailTemplates || [])
            .filter((emailTemplate) => emailTemplate.isSystemTemplate && !emailTemplate.isLastUsedTemplate)
            .map((emailTemplate) => ({
                value: emailTemplate.uuid,
                label: emailTemplate.name,
                isSystemTemplate: true,
            }));

        const blankOptions = (emailTemplates || [])
            .filter((emailTemplate) => emailTemplate.isBlankTemplate)
            .map((emailTemplate) => ({
                value: emailTemplate.uuid,
                label: emailTemplate.name,
                isBlankTemplate: true,
            }));

        const employerCampaignOptions = (emailTemplates || [])
            .filter((emailTemplate) => emailTemplate.isEmployerCampaignTemplate)
            .reduce((result, emailTemplate) => {
                if (emailTemplate.shouldAddGroupHeader) {
                    result.push({ isDivider: true, label: emailTemplate.campaignName });
                }
                result.push({
                    value: emailTemplate.uuid,
                    label: emailTemplate.name,
                    campaignId: emailTemplate.campaignId,
                });
                return result;
            }, []);

        const systemCampaignOptions = (emailTemplates || [])
            .filter((emailTemplate) => emailTemplate.isSystemCampaignTemplate)
            .reduce((result, emailTemplate) => {
                if (emailTemplate.shouldAddGroupHeader) {
                    result.push({ isDivider: true, label: emailTemplate.campaignName });
                }
                result.push({
                    value: emailTemplate.uuid,
                    label: emailTemplate.name,
                    campaignId: emailTemplate.campaignId,
                });
                return result;
            }, []);

        const metadataEmailTemplateOptions = chain(emailTemplates || [])
            .filter((emailTemplate) => emailTemplate.isMetadataTemplate)
            .orderBy(['order', 'name'], ['asc', 'asc'])
            .reduce((result, template) => [
                ...result,
                // eslint-disable-next-line max-len
                ...((last(result)?.viewType !== template.viewType)
                    ? [{ isDivider: true, label: template.translatedType || template.type, viewType: template.viewType }]
                    : []),
                {
                    label: template.translatedName || template.name,
                    value: template.uuid,
                    type: template.type,
                    canDelete: false,
                    viewType: template.viewType,
                },
            ], [])
            .value();

        const resultOptions = [];

        if (!isEmpty(lastTemplateUsedOptions)) {
            resultOptions.push({ isDivider: true, label: 'LAST_USED_TEMPLATES' });
            resultOptions.push(...lastTemplateUsedOptions);
        }

        if (!isEmpty(userEmailOptions)) {
            resultOptions.push({ isDivider: true, label: 'USER_EMAIL_TEMPLATES' });
            resultOptions.push(...userEmailOptions);
        }

        if (!isEmpty(currentJobCampaignOptions)) {
            resultOptions.push({ isDivider: true, label: 'CURRENT_JOB_CAMPAIGN_TEMPLATES' });
            resultOptions.push(...currentJobCampaignOptions);
        }

        if (!isEmpty(employerCampaignOptions)) {
            resultOptions.push(...employerCampaignOptions);
        }

        if (isUserRecruiter && !isEmpty(systemCampaignOptions)) {
            resultOptions.push(...systemCampaignOptions);
        }

        if (!isEmpty(systemEmailOptions)) {
            resultOptions.push({ isDivider: true, label: 'SYSTEM_EMAIL_TEMPLATES' });
            resultOptions.push(...systemEmailOptions);
        }

        if (!isEmpty(blankOptions)) {
            resultOptions.push({ isDivider: true });
            resultOptions.push(...blankOptions);
        }

        if (!isEmpty(metadataEmailTemplateOptions)) {
            resultOptions.push(...metadataEmailTemplateOptions);
        }

        return resultOptions;
    }, [emailTemplates, selectedTemplate, getAuthEntityType]);

    const onSelectChange = useCallback((e) => {
        const uuid = e.target.value;
        const template = find(emailTemplates, { uuid });

        trackEvent(TRACKING_EVENTS.EMAIL_EDITOR_SELECT_TEMPLATE, {
            positionId: position._id,
            positionName: position.jobTitle,
            employerId: position.employerId,
        });
        trackTag([HOTJAR_TAGS.EMAIL_EDITOR_SELECT_TEMPLATE]);

        onSelectedEmailTemplateChange(template);
    }, [emailTemplates, onSelectedEmailTemplateChange, selectedTemplate]);

    const onDeleteTemplate = useCallback((uuid) => {
        trackEvent(TRACKING_EVENTS.EMAIL_EDITOR_DELETE_TEMPLATE, {
            positionId: position._id,
            positionName: position.jobTitle,
            employerId: position.employerId,
        });
        trackTag([HOTJAR_TAGS.EMAIL_EDITOR_DELETE_TEMPLATE]);

        onDeleteEmailTemplate(uuid);
    }, [onDeleteEmailTemplate]);

    return (
        <CustomSelect
            noBorder
            value={selectedValue}
            onChange={onSelectChange}
            options={selectOptions}
            formControlProps={{
                fullWidth: true,
                className: rootClasses,
            }}
            renderOptions={({ options, className: optionClassName }) => (options || []).map((option, index) => {
                if (option.isDivider) {
                    return (
                        <MenuItem
                            key={index}
                            disabled
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                            classes={{ root: optionClassName }}
                        >
                            <div className={classes.optionContent}>
                                <div className={classes.dividerLabel}>
                                    {t(option.label)}
                                </div>
                            </div>
                        </MenuItem>
                    );
                }

                return (
                    <MenuItem
                        value={option.value}
                        key={index}
                        focusVisibleClassName='selected'
                        disabled={option.disabled}
                        classes={{ root: optionClassName }}
                    >
                        <div className={classes.optionContent}>
                            <div className={classes.optionLabel}>
                                {t(option.label)}
                            </div>
                            { option.value !== selectedValue && option.canDelete && <img src={DeleteIcon}
                                className={classes.deleteIcon}
                                onClick={() => {
                                    onDeleteTemplate(option.value);
                                }}
                            /> }
                        </div>
                    </MenuItem>
                );
            })}
        />
    );
};

EmailSelectComponent.propTypes = {
    className: PropTypes.string,
    emailTemplates: PropTypes.array,
    selectedTemplate: PropTypes.object,
    onSelectedEmailTemplateChange: PropTypes.func,
    onDeleteEmailTemplate: PropTypes.func,
    noBorder: PropTypes.bool,
};

export default memo(EmailSelectComponent);
